import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Ticket } from "lucide-react";
import { useTranslation } from "react-i18next";

const EMPTY = {
  code: "", description: "", type: "percent", value: 10,
  min_subtotal: 0, max_discount: 0, expires_at: "",
  usage_limit_total: 0, is_active: true,
};

export default function AdminCoupons() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get("/merchant/coupons").then(({ data }) => setItems(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...EMPTY }); setOpen(true); };
  const openEdit = (c) => {
    setEditing({
      ...c,
      expires_at: c.expires_at ? String(c.expires_at).slice(0, 10) : "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        code: editing.code,
        description: editing.description || "",
        type: editing.type,
        value: Number(editing.value),
        min_subtotal: Number(editing.min_subtotal || 0),
        max_discount: Number(editing.max_discount || 0),
        expires_at: editing.expires_at ? new Date(editing.expires_at + "T23:59:59").toISOString() : null,
        usage_limit_total: parseInt(editing.usage_limit_total || 0, 10),
        is_active: !!editing.is_active,
      };
      if (editing.id) await api.put(`/merchant/coupons/${editing.id}`, payload);
      else await api.post("/merchant/coupons", payload);
      toast.success(t("common.saved"));
      setOpen(false);
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const del = async (c) => {
    try { await api.delete(`/merchant/coupons/${c.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{t("coupons.title")}</h1>
          <p className="text-slate-500 text-sm mt-1">{t("coupons.subtitle")}</p>
        </div>
        <Button onClick={openNew} data-testid="coupons-new-btn" className="rounded-full bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" /> {t("coupons.newCoupon")}</Button>
      </div>

      <Card className="p-0 overflow-hidden border-slate-200/70">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">{t("common.loading")}</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center"><Ticket className="w-5 h-5 text-slate-400" /></div>
            <div className="mt-3 text-slate-500 text-sm">{t("coupons.empty")}</div>
            <Button onClick={openNew} className="mt-4 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="coupons-empty-cta"><Plus className="w-4 h-4 mr-1" /> {t("coupons.createCoupon")}</Button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((c) => (
              <div key={c.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`coupon-row-${c.code}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-nums font-bold text-slate-900 text-lg tracking-wider">{c.code}</span>
                    {c.is_active ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {c.type === "percent" ? `${c.value}% off` : `₹${c.value} off`}
                    {c.min_subtotal > 0 && ` · min ₹${c.min_subtotal}`}
                    {c.max_discount > 0 && c.type === "percent" && ` · max ₹${c.max_discount}`}
                    {c.expires_at && ` · exp ${new Date(c.expires_at).toLocaleDateString("en-IN")}`}
                  </div>
                  {c.description && <div className="text-xs text-slate-500 mt-1 italic">{c.description}</div>}
                </div>
                <div className="text-xs text-slate-500 font-mono-nums">
                  {c.usage_limit_total > 0 ? `${c.times_used} / ${c.usage_limit_total}` : `${c.times_used} used`}
                </div>
                <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`coupon-edit-${c.code}`}><Pencil className="w-4 h-4" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="outline" size="sm" data-testid={`coupon-delete-${c.code}`}><Trash2 className="w-4 h-4 text-red-600" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete coupon?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(c)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit coupon" : "New coupon"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Code</Label><Input data-testid="coupon-form-code" placeholder="WELCOME10" className="mt-1 font-mono-nums uppercase" value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} /></div>
              <div><Label>Description <span className="text-slate-400 text-xs">(shown to customer)</span></Label><Textarea rows={2} data-testid="coupon-form-desc" className="mt-1" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <select
                    data-testid="coupon-form-type"
                    className="mt-1 flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm outline-none"
                    value={editing.type}
                    onChange={(e) => setEditing({ ...editing, type: e.target.value })}
                  >
                    <option value="percent">Percent (%)</option>
                    <option value="flat">Flat (₹)</option>
                  </select>
                </div>
                <div>
                  <Label>{editing.type === "percent" ? "Percent value (0-100)" : "Flat discount (₹)"}</Label>
                  <Input type="number" data-testid="coupon-form-value" className="mt-1 font-mono-nums" value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Min order (₹) <span className="text-slate-400 text-xs">0 = none</span></Label>
                  <Input type="number" data-testid="coupon-form-min" className="mt-1 font-mono-nums" value={editing.min_subtotal} onChange={(e) => setEditing({ ...editing, min_subtotal: e.target.value })} />
                </div>
                <div>
                  <Label>Max discount (₹) <span className="text-slate-400 text-xs">percent cap</span></Label>
                  <Input type="number" data-testid="coupon-form-max" className="mt-1 font-mono-nums" value={editing.max_discount} onChange={(e) => setEditing({ ...editing, max_discount: e.target.value })} disabled={editing.type !== "percent"} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Expires on <span className="text-slate-400 text-xs">optional</span></Label>
                  <Input type="date" data-testid="coupon-form-expires" className="mt-1 font-mono-nums" value={editing.expires_at} onChange={(e) => setEditing({ ...editing, expires_at: e.target.value })} />
                </div>
                <div>
                  <Label>Usage limit <span className="text-slate-400 text-xs">0 = unlimited</span></Label>
                  <Input type="number" data-testid="coupon-form-limit" className="mt-1 font-mono-nums" value={editing.usage_limit_total} onChange={(e) => setEditing({ ...editing, usage_limit_total: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <Label>Active</Label>
                <Switch checked={editing.is_active} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} data-testid="coupon-form-active" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="coupon-form-save" className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
